import {
  AlertTriangle, Clock, Pill, ShieldAlert, ClipboardList,
  Repeat, Cake, MessageSquare, Calendar, Bell, UserCog, Flag, UserCheck, Briefcase,
} from 'lucide-react';
import moment from 'moment';

/**
 * Shared notification metadata used by the bell dropdown and the full list page:
 * which tab a type belongs to, which section it deep-links to, its icon/colour,
 * priority and time labels. One place so both views stay consistent.
 */

export const TABS = ['All', 'Clients', 'Rota', 'Team'];

// type → tab
const CATEGORY = {
  incident: 'Clients',
  medication_issue: 'Clients',
  high_risk: 'Clients',
  care_plan_review_due: 'Clients',
  risk_assessment_due: 'Clients',
  profile_change: 'Clients',
  client_flag_update: 'Clients',
  care_plan_update: 'Clients',
  birthday: 'Clients',
  missed_visit: 'Rota',
  service_change: 'Rota',
  rota_published: 'Rota',
  rota_change: 'Rota',
  first_visit_reminder: 'Rota',
  announcement: 'Team',
  event: 'Team',
  // Hiring is the team's business, not a client's.
  reference_received: 'Team',
  // Agency cover is about who works which shift.
  agency_request: 'Rota',
  agency_offer: 'Rota',
  agency_booking: 'Rota',
  agency_hours: 'Rota',
  agency_late: 'Rota',
  // A carer asked for an extra shift (Rota > Available Shifts).
  open_shift_request: 'Rota',
};

export const categoryOf = (type) => CATEGORY[type] || 'Team';

/**
 * Where clicking a notification should take the user — the appropriate SECTION,
 * not a detail popup. Deep-links to the resident when we know it.
 */
export const sectionFor = (n) => {
  // A notification that names its own page (the agency ones do) goes there.
  if (typeof n?.data?.link === 'string' && n.data.link.startsWith('/')) return n.data.link;
  const rid = n?.data?.residence_id;
  switch (n?.type) {
    case 'incident':
    case 'medication_issue':
    case 'high_risk':
    case 'care_plan_review_due':
    case 'risk_assessment_due':
    case 'profile_change':
    case 'client_flag_update':
    case 'care_plan_update':
    case 'birthday':
      return rid ? `/manager/residences/${rid}` : '/manager/residences';
    case 'service_change':
      return rid ? `/manager/residences/${rid}` : '/manager/care-packages';
    case 'missed_visit':
    case 'first_visit_reminder':
      return '/manager/domiciliary-schedule';
    case 'rota_published':
    case 'rota_change':
      return '/manager/duty-roster';
    case 'open_shift_request':
      return '/manager/available-shifts';
    case 'reference_received':
      return '/manager/recruitment';
    case 'announcement':
      return '/manager/announcements';
    case 'event':
      return '/manager/events';
    default:
      return '/notifications';
  }
};

// type → icon + colour classes (icon colour / soft background)
const ICONS = {
  incident: { Icon: AlertTriangle, ring: 'bg-rose-50 text-rose-500 dark:bg-rose-500/15' },
  missed_visit: { Icon: Clock, ring: 'bg-rose-50 text-rose-500 dark:bg-rose-500/15' },
  medication_issue: { Icon: Pill, ring: 'bg-amber-50 text-amber-500 dark:bg-amber-500/15' },
  high_risk: { Icon: ShieldAlert, ring: 'bg-rose-50 text-rose-500 dark:bg-rose-500/15' },
  risk_assessment_due: { Icon: ShieldAlert, ring: 'bg-amber-50 text-amber-500 dark:bg-amber-500/15' },
  care_plan_review_due: { Icon: ClipboardList, ring: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-500/15' },
  care_plan_update: { Icon: ClipboardList, ring: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-500/15' },
  client_flag_update: { Icon: Flag, ring: 'bg-violet-50 text-violet-500 dark:bg-violet-500/15' },
  profile_change: { Icon: UserCog, ring: 'bg-blue-50 text-blue-500 dark:bg-blue-500/15' },
  service_change: { Icon: Repeat, ring: 'bg-blue-50 text-blue-500 dark:bg-blue-500/15' },
  rota_published: { Icon: Calendar, ring: 'bg-blue-50 text-blue-500 dark:bg-blue-500/15' },
  rota_change: { Icon: Calendar, ring: 'bg-blue-50 text-blue-500 dark:bg-blue-500/15' },
  first_visit_reminder: { Icon: Clock, ring: 'bg-blue-50 text-blue-500 dark:bg-blue-500/15' },
  birthday: { Icon: Cake, ring: 'bg-pink-50 text-pink-500 dark:bg-pink-500/15' },
  announcement: { Icon: MessageSquare, ring: 'bg-indigo-50 text-indigo-500 dark:bg-indigo-500/15' },
  event: { Icon: Calendar, ring: 'bg-indigo-50 text-indigo-500 dark:bg-indigo-500/15' },
  reference_received: { Icon: UserCheck, ring: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-500/15' },
  // Staffing agencies (api/utils/agencyNotifications.js) — each carries its own data.link.
  agency_connection: { Icon: Briefcase, ring: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15' },
  agency_request: { Icon: ClipboardList, ring: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15' },
  agency_offer: { Icon: UserCheck, ring: 'bg-blue-50 text-blue-500 dark:bg-blue-500/15' },
  agency_booking: { Icon: Calendar, ring: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-500/15' },
  agency_staff: { Icon: UserCog, ring: 'bg-violet-50 text-violet-500 dark:bg-violet-500/15' },
  agency_hours: { Icon: Clock, ring: 'bg-blue-50 text-blue-500 dark:bg-blue-500/15' },
  agency_late: { Icon: AlertTriangle, ring: 'bg-rose-50 text-rose-500 dark:bg-rose-500/15' },
  open_shift_request: { Icon: Calendar, ring: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15' },
  default: { Icon: Bell, ring: 'bg-blue-50 text-blue-500 dark:bg-blue-500/15' },
};

export const iconFor = (type) => ICONS[type] || ICONS.default;

export const priorityOf = (n) => n?.data?.priority || 'medium';
export const isHighPriority = (n) => priorityOf(n) === 'high';

/** Short timestamp: "Now" / "14:20" / "Yesterday" / "3 Jul". */
export const timeLabel = (createdAt) => {
  const d = moment(createdAt);
  const now = moment();
  if (now.diff(d, 'seconds') < 60) return 'Now';
  if (d.isSame(now, 'day')) return d.format('HH:mm');
  if (d.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.format('D MMM');
};

/** Section header bucket: TODAY vs EARLIER. */
export const dayGroup = (createdAt) =>
  moment(createdAt).isSame(moment(), 'day') ? 'TODAY' : 'EARLIER';
